/* packages/volto-castle-theme/src/components/HPPress/HPPress.tsx */
import React from 'react';

import thumb1 from '../../../static/img/testimonial_thumb1.png';

type Testimonial = {
  name: string;
  role: string;
  quote: string;
  thumb: string;
  socials?: { label: string; href: string; kind: 'in' | 'p' | 'g' }[];
};

const slides: Testimonial[] = [
  {
    name: 'Arix Wonder',
    role: 'CEO OF LVATO',
    quote:
      'CastleCMS.io gave our team a clean editing experience and a site that stays fast even as we add more content. We launched updates in days instead of weeks, without sacrificing design or structure.',
    thumb: thumb1,
    socials: [
      { label: 'LinkedIn', href: 'https://www.linkedin.com/', kind: 'in' },
      { label: 'Pinterest', href: 'https://www.pinterest.com/', kind: 'p' },
      { label: 'Google Plus', href: 'https://plus.google.com/', kind: 'g' },
    ],
  },
  {
    name: 'John Smith',
    role: 'Full Stack Developer',
    quote:
      'With CastleCMS.io, deployment is predictable and content changes don’t require engineering time for every small tweak. The frontend stays flexible while the CMS keeps everything organized and easy to govern.',
    thumb: thumb1,
    socials: [
      { label: 'LinkedIn', href: 'https://www.linkedin.com/', kind: 'in' },
      { label: 'Pinterest', href: 'https://www.pinterest.com/', kind: 'p' },
      { label: 'Google Plus', href: 'https://plus.google.com/', kind: 'g' },
    ],
  },
  {
    name: 'Greggory Lee',
    role: 'Designer at Apple',
    quote:
      'CastleCMS.io made it simple to keep our brand consistent across pages with reusable blocks and polished layouts. It feels like we finally have a CMS that supports design quality instead of fighting it.',
    thumb: thumb1,
    socials: [
      { label: 'LinkedIn', href: 'https://www.linkedin.com/', kind: 'in' },
      { label: 'Pinterest', href: 'https://www.pinterest.com/', kind: 'p' },
      { label: 'Google Plus', href: 'https://plus.google.com/', kind: 'g' },
    ],
  },
];

const SocialIcon: React.FC<{ kind: 'in' | 'p' | 'g' }> = ({ kind }) => {
  const text = kind === 'in' ? 'in' : kind === 'p' ? 'p' : 'g+';
  return <span className={`hp-press__socialIcon hp-press__socialIcon--${kind}`}>{text}</span>;
};

type DragState = {
  pointerId: number | null;
  startX: number;
  startY: number;
  deltaX: number;
  isDragging: boolean;
  hasLockedAxis: boolean;
  isHorizontal: boolean;

  lastX: number;
  lastT: number;
  velocityPxPerMs: number;
};

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

const AUTO_MS = 6000;
const RESUME_DELAY_MS = 700;

const MOMENTUM_PROJECT_MS = 220;
const MOMENTUM_MAX_EXTRA_SLIDES = 2;

const LOOP = true; // (a) set false if you want hard ends + rubber-band
const RUBBER_BAND = true; // (b) edge resistance when LOOP === false

const mod = (n: number, m: number) => ((n % m) + m) % m;

const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const rubberBand = (dx: number, width: number) => {
  const w = Math.max(1, width);
  const abs = Math.abs(dx);
  const sign = dx < 0 ? -1 : 1;
  const capped = (abs * 0.55 * w) / (abs + 0.55 * w);
  return sign * capped;
};

const HPPress: React.FC = () => {
  const [active, setActive] = React.useState(0);

  const [isDragging, setIsDragging] = React.useState(false);
  const [dragOffsetPx, setDragOffsetPx] = React.useState(0);

  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [isPointerDown, setIsPointerDown] = React.useState(false);

  const total = slides.length;
  const viewportRef = React.useRef<HTMLDivElement | null>(null);

  const dragRef = React.useRef<DragState>({
    pointerId: null,
    startX: 0,
    startY: 0,
    deltaX: 0,
    isDragging: false,
    hasLockedAxis: false,
    isHorizontal: false,
    lastX: 0,
    lastT: 0,
    velocityPxPerMs: 0,
  });

  const resumeTimerRef = React.useRef<number | null>(null);

  const prefersReducedMotion = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  }, []);

  const goTo = React.useCallback(
    (idx: number) => {
      if (total <= 0) return;
      setActive(() => (LOOP ? mod(idx, total) : clamp(idx, 0, total - 1)));
    },
    [total],
  );

  const next = React.useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = React.useCallback(() => goTo(active - 1), [active, goTo]);

  const isPaused = isHovered || isFocused || isDragging || isPointerDown;

  React.useEffect(() => {
    if (prefersReducedMotion) return;
    if (isPaused) return;
    const id = window.setInterval(() => goTo(active + 1), AUTO_MS);
    return () => window.clearInterval(id);
  }, [isPaused, goTo, prefersReducedMotion, active]);

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    },
    [prev, next],
  );

  const clearResumeTimer = React.useCallback(() => {
    if (resumeTimerRef.current != null) {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  }, []);

  const onPointerDown = React.useCallback(
    (e: React.PointerEvent) => {
      const el = viewportRef.current;
      if (!el) return;

      // ✅ Don’t hijack clicks on interactive elements (links/buttons/inputs/etc.)
      const target = e.target as HTMLElement | null;
      const interactive = target?.closest?.(
        'a,button,input,textarea,select,option,[role="button"],[data-no-drag]',
      );
      if (interactive) {
        return;
      }

      clearResumeTimer();

      const t = nowMs();

      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        deltaX: 0,
        isDragging: true,
        hasLockedAxis: false,
        isHorizontal: false,
        lastX: e.clientX,
        lastT: t,
        velocityPxPerMs: 0,
      };

      setIsPointerDown(true);
      setIsDragging(true);
      setDragOffsetPx(0);

      el.setPointerCapture(e.pointerId);
    },
    [clearResumeTimer],
  );

  const onPointerMove = React.useCallback(
    (e: React.PointerEvent) => {
      const st = dragRef.current;
      if (!st.isDragging || st.pointerId !== e.pointerId) return;

      const dxRaw = e.clientX - st.startX;
      const dy = e.clientY - st.startY;

      if (!st.hasLockedAxis) {
        const dist = Math.hypot(dxRaw, dy);
        if (dist < 6) return;
        st.hasLockedAxis = true;
        st.isHorizontal = Math.abs(dxRaw) >= Math.abs(dy);
      }

      if (!st.isHorizontal) return;

      const t = nowMs();
      const dt = Math.max(1, t - st.lastT);
      st.velocityPxPerMs = (e.clientX - st.lastX) / dt;
      st.lastX = e.clientX;
      st.lastT = t;

      const width = viewportRef.current?.getBoundingClientRect().width ?? 1;

      let dx = dxRaw;
      if (!LOOP && RUBBER_BAND) {
        const atFirst = active === 0;
        const atLast = active === total - 1;
        if ((atFirst && dxRaw > 0) || (atLast && dxRaw < 0)) {
          dx = rubberBand(dxRaw, width);
        }
      }

      st.deltaX = dx;
      setDragOffsetPx(dx);
    },
    [active, total],
  );

  const computeMomentumTarget = React.useCallback(
    (dx: number, width: number, velocityPxPerMs: number) => {
      const projectedDx = dx + velocityPxPerMs * MOMENTUM_PROJECT_MS;

      const progressSlides = -projectedDx / Math.max(1, width);
      let deltaSlides = Math.round(progressSlides);

      deltaSlides = clamp(deltaSlides, -MOMENTUM_MAX_EXTRA_SLIDES, MOMENTUM_MAX_EXTRA_SLIDES);

      const tinyMove = Math.abs(dx) < Math.max(40, width * 0.08);
      const tinyVel = Math.abs(velocityPxPerMs) < 0.15;
      if (tinyMove && tinyVel) return active;

      const target = active + deltaSlides;
      return LOOP ? mod(target, total) : clamp(target, 0, total - 1);
    },
    [active, total],
  );

  const finishDrag = React.useCallback(
    (pointerId: number) => {
      const st = dragRef.current;
      if (!st.isDragging || st.pointerId !== pointerId) return;

      const width = viewportRef.current?.getBoundingClientRect().width ?? 1;

      const dx = st.deltaX;
      const v = st.velocityPxPerMs;

      st.isDragging = false;
      st.pointerId = null;

      setIsDragging(false);
      setIsPointerDown(false);
      setDragOffsetPx(0);

      if (st.isHorizontal) {
        const target = computeMomentumTarget(dx, width, v);
        goTo(target);
      }

      clearResumeTimer();
      resumeTimerRef.current = window.setTimeout(() => {
        resumeTimerRef.current = null;
      }, RESUME_DELAY_MS);
    },
    [computeMomentumTarget, goTo, clearResumeTimer],
  );

  const onPointerUp = React.useCallback(
    (e: React.PointerEvent) => finishDrag(e.pointerId),
    [finishDrag],
  );

  const onPointerCancel = React.useCallback(
    (e: React.PointerEvent) => finishDrag(e.pointerId),
    [finishDrag],
  );

  const onTouchStart = React.useCallback(() => {
    clearResumeTimer();
    setIsPointerDown(true);
  }, [clearResumeTimer]);

  const onTouchEnd = React.useCallback(() => {
    setIsPointerDown(false);
  }, []);

  React.useEffect(() => {
    return () => clearResumeTimer();
  }, [clearResumeTimer]);

  const trackStyle: React.CSSProperties = {
    transform: `translateX(calc(-${active * 100}% + ${dragOffsetPx}px))`,
  };

  const current = slides[active];

  return (
    <section className="hp-press" aria-label="In the press">
      <div className="hp-press__container">
        <header className="hp-press__heading">
          <h2 className="hp-press__title">IN THE PRESS</h2>
          <div className="hp-press__titleRule" />
        </header>

        <div className="hp-press__cardWrap">
          <div className="hp-press__thumb" aria-hidden="true">
            <img
              key={`${current.thumb}-${current.name}-${active}`}
              className="hp-press__thumbImg"
              src={current.thumb}
              alt=""
            />
          </div>

          <div
            ref={viewportRef}
            className={`hp-press__viewport ${isDragging ? 'is-dragging' : ''}`}
            role="region"
            aria-roledescription="carousel"
            aria-label="Testimonials"
            tabIndex={0}
            onKeyDown={onKeyDown}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onTouchCancel={onTouchEnd}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          >
            <div className="hp-press__track" style={trackStyle}>
              {slides.map((t, i) => (
                <div
                  key={`${t.name}-${i}`}
                  className="hp-press__slide"
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${i + 1} of ${total}`}
                >
                  <div className="hp-press__card">
                    <div className="hp-press__meta">
                      <div className="hp-press__name">{t.name}</div>
                      <div className="hp-press__role">{t.role}</div>

                      {t.socials?.length ? (
                        <div className="hp-press__socials" aria-label="Social links">
                          {t.socials.map((s, idx) => (
                            <a
                              key={`${s.kind}-${idx}`}
                              className="hp-press__socialLink"
                              href={s.href}
                              aria-label={s.label}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-no-drag
                              onPointerDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <SocialIcon kind={s.kind} />
                            </a>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    <div className="hp-press__quote">
                      <span className="hp-press__quoteMark" aria-hidden="true">
                        “
                      </span>
                      <p className="hp-press__quoteText">{t.quote}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="hp-press__dots" role="tablist" aria-label="Testimonial slides">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`hp-press__dot ${i === active ? 'is-active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1} of ${total}`}
              aria-selected={i === active}
              role="tab"
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HPPress;